import ModuleRaid from "moduleraid";
import type { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import get from "lodash/get";
import type { EventEmitter } from "node:stream";
import type { Message, ModelType, XIdType } from "@whatslly/types/instant-messaging/WhatsappTypes";

export interface WhatsAppApi {
  moduleRaid?: ModuleRaid;
  Chat: EventEmitter & {
    getModelsArray(): ModelType[];
    find(contactId: any): Promise<ModelType | undefined>;
    get(lid: string): Promise<any>;
  };
  WAWebNotificationTone: {
    playNotification: () => void;
  };
  Contact: {
    getModelsArray(): ModelType["contact"][];
    get(lid: string | XIdType): Promise<ModelType["contact"]>;
  };
  ConversationMsgs: {
    loadEarlierMsgs: (chat: any) => Promise<Message[]>;
  };
  Msg: EventEmitter & {
    getMessagesById: (ids: string[]) => { messages: Message[] };
  };
  MessageInfo: {
    sendQueryMsgInfo: (message: Message) => Promise<any>;
  };
  Group: {
    queryAllGroups: () => Promise<any>;
  };
  Downloads: {
    downloadAndMaybeDecrypt: (opts: {
      directPath?: string;
      encFilehash?: string;
      filehash?: string;
      mediaKey?: string;
      mediaKeyTimestamp?: number;
      type?: string;
      signal: AbortSignal;
    }) => Promise<ArrayBuffer>;
  };
  Clock: {
    relativeDateStr: (time: number) => string;
  };
  NewsLetter: EventEmitter & {
    getMessagesById(): (ids: string[]) => { messages: Message[] };
  };
  CallCollection: EventEmitter & {
    activeCall: any;
  };
  Media: {
    getSignedUrl: (opts: {
      directPath?: string | null;
      encFilehash?: string;
      filehash?: string;
      mediaKey?: string;
      mediaKeyTimestamp?: number;
      type?: string;
      mode?: string;
      hostname?: string;
    }) => Promise<string>;
    operations: {
      download: string;
      upload: string;
    };
    mediaHosts: {
      getHostsInfo: (opts: {
        operation: string;
        directPath?: string;
        encFilehash?: string;
        type?: string;
        signal: AbortSignal;
        forceRefresh: boolean;
      }) => Promise<{
        selectedHost: string;
        fallbackHost: string;
      }>;
    };
    selectHost: (opts: {
      selectedHost: string;
      fallbackHost: string;
      attemptCount: number;
      lastHostUsed: string | null;
      lastFetchMadeProgress: boolean;
    }) => {
      hostname: string;
    };
  };
  Status: EventEmitter & {
    getMessagesById: (ids: string[]) => { messages: Message[] };
  };
  Utils: {
    lidToUser: (lid: XIdType) => XIdType;
    userToLid: (user: XIdType) => XIdType;
  };
}

let injeted: WhatsAppApi | undefined;
export function injectWhatsappApi(selectors: WhatsappSelectorsType): WhatsAppApi {
  if (injeted) return injeted;
  const moduleRaid = new ModuleRaid({ entrypoint: selectors.jsonPModuleName });
  const whatsappAPI = Object.assign(
    { moduleRaid },
    get(moduleRaid.findModule((m) => selectors.chat.find.every((path) => get(m, path)))[0], selectors.chat.module)
  ) as any;
  whatsappAPI.ConversationMsgs = moduleRaid.findModule((m) => selectors.ConversationMsgs.find.every((path) => get(m, path)))[0] as any;
  whatsappAPI.Downloads = get(
    moduleRaid.findModule((m) => selectors.downloadManager.find.every((path) => get(m, path)))[0],
    selectors.downloadManager.module
  );
  whatsappAPI.Clock = get(moduleRaid.findModule((m) => selectors.clock.find.every((path) => get(m, path)))[0], selectors.clock.module);
  const messageInfo = moduleRaid.findModule((m) => selectors.ack.find.every((path) => get(m, path)))[0];
  whatsappAPI.MessageInfo = {
    sendQueryMsgInfo: (message: Message) => {
      return get(messageInfo, selectors.ack.fn)(message.id);
    },
  };
  injeted = whatsappAPI;
  return whatsappAPI;
}
